/** @prettier */

import {Box, Container, Grid, Stack, Paper} from '@mui/material';
import {LoaderFunctionArgs, useLoaderData, useParams} from 'react-router-dom';
import {useEffect, useState} from 'react';
import {utcToZonedTime} from 'date-fns-tz';
import {useTranslation} from 'react-i18next';

//MTH component import
import {ScheduleItem} from './ScheduleItem';
import APIErrorMessage from '../../component/APIErrorMessage';
import SingleGroupSelectBox from './GroupSelectBox';
import GearMenu, {GearMenuListArgType} from '../../component/GearMenuList';
import ScheduleSettingDialog from './ScheduleSettingDialog';
import CopyToDialog from './CopyToDialog';
import ImportTemplateDialog from './ImportTemplateDialog';
import {AddButton} from '../../component/BasicButton';
//API import
import {
  getCompositionsGroups,
  getCompositionsSitesBySiteID,
  getTodaySchedule,
} from '../../API/API_Collection';
//interface import
import * as apiIF from '../../Type/api';
//Style import
import '../../css/common/common.css';

import {limit} from '../../define';
import {checkUnitExistence} from '../../functions/checkUnitExistence';
import type {unitExistence} from '../../functions/checkUnitExistence';

type LoaderReturnType = {
  /** サイト名称 */
  siteName: apiIF.res.getCompositionsSitesBySiteID['site-name'];
  /** タイムゾーン */
  timeZone: apiIF.res.getCompositionsSitesBySiteID['timezone'];
  /** グループ情報 */
  groupInfo: apiIF.res.getCompositionsGroups['group-informations'];
};
export async function loader({
  params,
}: LoaderFunctionArgs): Promise<LoaderReturnType | undefined> {
  //パスパラメータを取得できない場合
  if (params.siteID === undefined) {
    return undefined;
  }

  try {
    const promises = [];
    //サイトの情報を取得
    promises.push(getCompositionsSitesBySiteID(params.siteID));
    //グループの情報の取得
    promises.push(
      getCompositionsGroups(params.siteID, {
        'fields': 'group-name,unit-system',
        'max-result': limit.REGISTRATION_NUM_GROUP_MAX,
      })
    );
    //ユニットの登録状況を取得
    promises.push(checkUnitExistence(params.siteID, true));
    const response = await Promise.all(promises);

    const siteInfo = response[0] as apiIF.res.getCompositionsSitesBySiteID;
    const groupInfo = response[1] as apiIF.res.getCompositionsGroups;
    const unitExistence = response[2] as unitExistence;

    if (
      unitExistence.hasAirconIU === false ||
      (siteInfo['site-authority'] !== 'admin' &&
        siteInfo['site-authority'] !== 'manager')
    ) {
      throw new Error(
        'Required equipment not registered or insufficient permission'
      );
    }

    return {
      siteName: siteInfo['site-name'],
      timeZone: siteInfo['timezone'],
      groupInfo: groupInfo['group-informations'].filter(group => {
        //本来はバックエンド側でやるべき
        //2ndStepではリリース優先でフロントエンド側で行った
        return group['unit-system'] === 'SL';
      }),
    };
  } catch {
    return undefined;
  }
}

/**
 * 当日スケジュール
 */
const TodaySchedule = () => {
  const trans = useTranslation().t; //翻訳関数
  //loaderのデータを取得
  const loaderData = useLoaderData() as LoaderReturnType | undefined;
  //パスパラメータを取得
  const {siteID} = useParams();

  /** サイト名称 */
  const [siteName, setSiteName] = useState<LoaderReturnType['siteName']>('');
  /** 表示する日付 */
  const [todayDisplay, setTodayDisplay] = useState(new Date());
  //prettier-ignore
  /** グループ情報 */
  const [groupInfo, setGroupInfo] = useState<LoaderReturnType['groupInfo'] | null>(null);
  /** セレクトボックスで選択したグループID */
  const [groupID, setGroupID] = useState('');
  /** スケジュール追加ダイアログ表示フラグ */
  const [isOpenAddDialog, setIsOpenAddDialog] = useState(false);
  /** 指定した特定日のスケジュールを当日スケジュールにコピーするダイアログ表示フラグ */
  const [isImportTemplateDialog, setIsImportTemplateDialog] = useState(false);
  /** 他のグループの当日スケジュールにコピーするダイアログ表示フラグ*/
  const [isCopyToDialog, setIsCopyToDialog] = useState(false);
  //prettier-ignore
  /** 当日スケジュールの設定データ */
  const [todayScheduleData, setTodayScheduleData] = useState<apiIF.res.getTodaySchedule['today-schedule']>([]);
  /** APIエラー時エラーメッセージ表示 */
  const [isOpenErrorDialog, setIsOpenErrorDialog] = useState(false);

  useEffect(() => {
    if (loaderData === undefined) return;

    setSiteName(loaderData.siteName);
    setTodayDisplay(utcToZonedTime(new Date(), loaderData.timeZone));
    setGroupInfo(loaderData.groupInfo);
    if (loaderData.groupInfo.length > 0) {
      setGroupID(loaderData.groupInfo[0]['group-id']);
    }
  }, [loaderData]);

  //スケジュール設定の取得
  useEffect(() => {
    if (siteID === undefined) return;
    if (groupID === '') return;

    //当日スケジュール設定の取得
    getTodaySchedule(siteID, groupID).then(response => {
      //Body用の変数に現在の状態を格納
      setTodayScheduleData(response['today-schedule']);
    });
  }, [siteID, groupID]);

  /** スケジュール更新関数 */
  const scheduleChangeHandler = () => {
    if (siteID === undefined) return;

    //当日スケジュール設定の取得
    getTodaySchedule(siteID, groupID).then(response => {
      //Body用の変数に現在の状態を格納
      setTodayScheduleData(response['today-schedule']);
    });
  };
  /**
   * ADD SCHEDULEボタン押下時処理
   */
  const handleAddSchedule = () => {
    setIsOpenAddDialog(true);
  };

  /**
   * Import template押下時処理
   */
  const importTemplateDialog = () => {
    setIsImportTemplateDialog(true);
  };

  /**
   * Copy to押下時処理
   */
  const copyToDialog = () => {
    setIsCopyToDialog(true);
  };

  /**
   * 歯車メニューリスト
   * @param groupID グループID
   * @returns メニュー一覧
   */
  const gearMenuItem = (groupID: string): GearMenuListArgType['menuItem'] => {
    return [
      {
        id: `mv-importTemplate${groupID}`,
        label: trans('TodaySchedule.gearMenu.importTemplate'),
        onClick: () => importTemplateDialog(),
      },
      {
        id: `copyTo${groupID}`,
        label: trans('TodaySchedule.gearMenu.copyTo'),
        onClick: () => copyToDialog(),
      },
    ];
  };

  /**
   * 月名に変換
   * @param month 月
   * @returns 月名
   */
  const convertMonthForDisplay = (month: number) => {
    switch (month) {
      case 0:
        return trans('TodaySchedule.monthName.january');
      case 1:
        return trans('TodaySchedule.monthName.february');
      case 2:
        return trans('TodaySchedule.monthName.march');
      case 3:
        return trans('TodaySchedule.monthName.april');
      case 4:
        return trans('TodaySchedule.monthName.may');
      case 5:
        return trans('TodaySchedule.monthName.june');
      case 6:
        return trans('TodaySchedule.monthName.july');
      case 7:
        return trans('TodaySchedule.monthName.august');
      case 8:
        return trans('TodaySchedule.monthName.september');
      case 9:
        return trans('TodaySchedule.monthName.october');
      case 10:
        return trans('TodaySchedule.monthName.november');
      case 11:
        return trans('TodaySchedule.monthName.december');
    }
  };

  if (siteID === undefined) return <></>;
  return (
    <Container fixed>
      <Stack>
        {/** タイトル */}
        <Box>
          <h1>{trans('TodaySchedule.title')}</h1>
        </Box>
        {/** スケジュール */}
        <Box className="schedule__contents">
          <Grid container rowSpacing={3} justifyContent="center">
            {/** サイトの名前 */}
            <Grid item xs={12} className="schedule__contents__siteName">
              {siteName}
            </Grid>
            {/** グループ選択欄 */}
            <Grid
              item
              xs={12}
              container
              alignItems="flex-end"
              columnSpacing={5}
              className="schedule__contents__groupSelectArea">
              {/** 日付 */}
              <Grid item xs={3}>
                {trans('TodaySchedule.date', {
                  year: todayDisplay.getFullYear(),
                  month: convertMonthForDisplay(todayDisplay.getMonth()),
                  day: todayDisplay.getDate(),
                })}
              </Grid>
              {/** グループ選択 */}
              <Grid item xs="auto">
                {trans('TodaySchedule.group')}
              </Grid>
              <Grid item xs>
                {groupID !== '' && groupInfo !== null && (
                  <SingleGroupSelectBox
                    groupID={groupID}
                    setGroupID={setGroupID}
                    groupInfo={groupInfo}
                  />
                )}
              </Grid>
              {/** 歯車アイコン */}
              {/** グループIDが空欄の場合は歯車アイコンを表示しない */}
              <Grid item xs="auto">
                {groupID !== '' && (
                  <GearMenu menuItem={gearMenuItem(groupID)} />
                )}
              </Grid>
            </Grid>
            {/** スケジュール追加ボタン */}
            <Grid item xs={12} className="schedule__contents__addButton">
              <AddButton
                onClick={handleAddSchedule}
                id="mv-btnAddSchedule"
                disabled={
                  groupID === '' ||
                  /* スケジュールが16個以上の場合は追加ボタンを非アクティブ化 */
                  todayScheduleData.length >= limit.MAX_NUM_SETTING_SCHEDULE
                }
                buttonText={trans('TodaySchedule.addSchedule')}
              />
            </Grid>
            {/** 一覧 */}
            <Grid item xs={8}>
              <TodayScheduleView
                siteID={siteID}
                groupID={groupID}
                schedule={todayScheduleData}
                scheduleChangeHandler={scheduleChangeHandler}
              />
            </Grid>
          </Grid>
        </Box>
      </Stack>
      {groupID !== '' && (
        <>
          {/** スケジュールを追加するダイアログ */}
          <ScheduleSettingDialog
            targetGroupID={groupID}
            schedule={{'command-time': '12:00'}}
            isOpenDialog={isOpenAddDialog}
            setIsOpenDialog={setIsOpenAddDialog}
            scheduleChangeHandler={scheduleChangeHandler}
            allTodaySchedule={todayScheduleData}
          />
          {/** 特定日のスケジュールから当日スケジュールを作成するダイアログ */}
          <ImportTemplateDialog
            groupID={groupID}
            isOpenDialog={isImportTemplateDialog}
            setIsOpenDialog={setIsImportTemplateDialog}
            scheduleChangeHandler={scheduleChangeHandler}
          />
        </>
      )}
      {/** 当日スケジュールを他グループの当日スケジュールへコピーするダイアログ */}
      {groupID !== '' && groupInfo !== null && (
        <CopyToDialog
          isOpenDialog={isCopyToDialog}
          setIsOpenDialog={setIsCopyToDialog}
          scheduleChangeHandler={scheduleChangeHandler}
          todayScheduleData={todayScheduleData}
          groupID={groupID}
          groupInfo={groupInfo}
        />
      )}
      {/* APIエラー時のメッセージ */}
      <APIErrorMessage
        open={isOpenErrorDialog}
        setOpen={setIsOpenErrorDialog}
        id={'mv-todayScheduleSaveErrDlgBtn'}
      />
    </Container>
  );
};

/**
 * 当日スケジュールを表示するコンポーネント
 * @returns JSX.Element
 */
const TodayScheduleView = (props: {
  /** 表示するグループが登録されているサイトのID */
  siteID: string;
  /** 表示するグループのID */
  groupID: string;
  /** 当日スケジュール */
  schedule: apiIF.res.getTodaySchedule['today-schedule'];
  /** スケジュール更新関数 */
  scheduleChangeHandler: () => void;
}) => {
  const trans = useTranslation().t; //翻訳関数

  /** 選択状態のスケジュール行 */
  const [selectedRow, setSelectedRow] = useState<number | string>('');

  if (props.groupID === '') return <></>;
  if (props.schedule.length === 0) {
    return <Box>{trans('TodaySchedule.notSet')}</Box>;
  }
  return (
    <Stack component={Paper} className="schedule__table">
      {/** ヘッダ */}
      <Grid container alignItems="center" className="schedule__table__header">
        {/** 時刻 */}
        <Grid item xs={2.5} className="schedule__table__header__item">
          {trans('TodaySchedule.list.time')}
        </Grid>
        {/** 指令値 */}
        <Grid item xs={4.5} className="schedule__table__header__item">
          {trans('TodaySchedule.list.action')}
        </Grid>
        {/** 操作アイコン */}
        <Grid item xs className="schedule__table__header__item">
          {trans('TodaySchedule.list.operation')}
        </Grid>
      </Grid>
      {/** ボディ */}
      {/** 設定済みのスケジュールを表示する */}
      <Stack className="schedule__table__body--todaySchedule">
        {props.schedule.map((schedule, index) => {
          return (
            <Box>
              <ScheduleItem
                key={`today-schedule${index}`}
                scheduleItemData={schedule}
                groupID={props.groupID}
                index={index}
                scheduleChangeHandler={props.scheduleChangeHandler}
                selectedRow={selectedRow}
                setSelectedRow={setSelectedRow}
                todayScheduleData={props.schedule}
              />
            </Box>
          );
        })}
      </Stack>
    </Stack>
  );
};

export default TodaySchedule;
